
import {
  ArrowLeft,
  Search,
  Star,
  Calendar,
  FileText,
  PanelLeftOpen,
  X,
} from 'lucide-react';
import Link from '@/components/compat/Link';
import { useEffect, useMemo, useState } from 'react';
import { ArticleAuthorCard } from '@/components/help-center/ArticleAuthorCard';
import { resolveArticleAuthorCardForGuide } from '@/lib/article-author';
import { ArticleFeedbackSection } from '@/components/help-center/ArticleFeedbackSection';
import { GuideRelatedSection } from '@/components/help-center/GuideRelatedSection';
import { InfoBox } from './ContentComponents';
import { ArticleHtmlChunk, renderArticleBlock } from '@/components/help-center/ArticleBodyRender';
import {
  buildPublicGuideList,
  findPublicGuideListItem,
  resolvePublicGuideContent,
  type PublicGuideListItem,
} from '@/lib/cms-guides';
import { useCms } from '@/components/painel/cms/cms-context';
import { XatErrorsGuide } from '@/components/help-center/guides/XatErrorsGuide';
import { XatTicketsGuide } from '@/components/help-center/guides/XatTicketsGuide';
import { getGuideCategoryHref, getGuideHref } from '@/lib/guide-routes';
import { isXatErrorsGuideArticle } from '@/lib/xat-errors-data';
import { isXatTicketsGuideArticle } from '@/lib/xat-tickets-data';
import { recordGuideView } from '@/services/guide-views.service';
import { analytics } from '@/services/analytics';
import { cn } from '@/lib/utils';
import { CmsPublicEditButton } from '@/components/help-center/CmsPublicEditButton';

interface ArticlesViewProps {
  category: string;
  articleSlug: string;
  onBack?: () => void;
  onBackToCategory?: () => void;
}

function ArticlesSidebar({
  open,
  onClose,
  categoryId,
  categoryLabel,
  articles: categoryArticles,
  selectedSlug,
  searchQuery,
  onSearchQueryChange,
}: {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  categoryLabel: string;
  articles: PublicGuideListItem[];
  selectedSlug: string;
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
}) {
  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-[#030023]/40 backdrop-blur-[2px] transition-opacity duration-300 lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={onClose}
        aria-hidden={!open}
      />

      <aside
        id="articles-sidebar"
        className={cn(
          'fixed left-0 top-20 z-50 flex h-[calc(100vh-5rem)] w-[min(100vw-3rem,20rem)] flex-col border-r border-gray-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
        aria-hidden={!open}
        aria-label="Artigos nesta seção"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4">
          <div className="min-w-0">
            <h3 className="font-poppins text-sm font-bold text-gray-900">Artigos nesta seção</h3>
            <p className="mt-0.5 truncate font-inter text-xs text-gray-500">{categoryLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            aria-label="Fechar lista de artigos"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-gray-100 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              placeholder="Buscar artigos..."
              className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        <ul className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto">
          {categoryArticles.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-gray-500">Nenhum artigo encontrado.</li>
          ) : (
            categoryArticles.map((article) => (
              <li key={article.key}>
                <Link
                  href={getGuideHref(categoryId, article.slug)}
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
                      onClose();
                    }
                  }}
                  className={cn(
                    'block w-full px-4 py-3.5 text-left transition-colors hover:bg-blue-50',
                    selectedSlug === article.slug && 'border-l-4 border-blue-600 bg-blue-50 pl-3'
                  )}
                >
                  <div className="mb-1 line-clamp-2 font-medium text-gray-900">{article.title}</div>
                  {article.popular ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                      <Star className="h-3 w-3 fill-amber-500" />
                      Popular
                    </span>
                  ) : null}
                </Link>
              </li>
            ))
          )}
        </ul>
      </aside>
    </>
  );
}

export default function ArticlesView({
  category,
  articleSlug,
  onBack,
  onBackToCategory,
}: ArticlesViewProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { articles: cmsArticles, categories, site, hydrated } = useCms();

  const categoryLabel =
    categories.find((c) => c.id === category)?.name ?? category;

  const categoryArticles = useMemo(() => {
    if (!hydrated) return [];
    return buildPublicGuideList(category, cmsArticles);
  }, [hydrated, category, cmsArticles]);

  const selectedItem = useMemo(() => {
    if (!articleSlug || !hydrated) return null;
    return findPublicGuideListItem(category, articleSlug, cmsArticles);
  }, [hydrated, category, articleSlug, cmsArticles]);

  const filteredArticles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return categoryArticles;
    return categoryArticles.filter((a) => a.title.toLowerCase().includes(q));
  }, [categoryArticles, searchQuery]);

  const resolvedContent = useMemo(() => {
    if (!selectedItem || !hydrated) return { title: '', body: undefined };
    return resolvePublicGuideContent(category, selectedItem, cmsArticles);
  }, [selectedItem, hydrated, category, cmsArticles]);

  const resolvedTitle = resolvedContent.title;
  const resolvedBody = resolvedContent.body;

  const authorCardProps = useMemo(() => {
    if (!selectedItem) return null;
    return resolveArticleAuthorCardForGuide({
      categoryId: category,
      item: selectedItem,
      cmsArticles: hydrated ? cmsArticles : [],
      body: resolvedBody,
      painelUsers: undefined,
      staffMembers: hydrated ? site.staffMembers : undefined,
    });
  }, [selectedItem, category, cmsArticles, hydrated, resolvedBody, site.staffMembers]);

  const [liveViews, setLiveViews] = useState<number | null>(null);

  useEffect(() => {
    if (!selectedItem) {
      setLiveViews(null);
      return;
    }
    setLiveViews(selectedItem.views);
    let cancelled = false;
    void recordGuideView(category, selectedItem.slug).then((result) => {
      if (cancelled || !result) return;
      setLiveViews(result.views);
    });
    void analytics.guideViewed({
      guideId: selectedItem.cmsId ?? selectedItem.key,
      categorySlug: category,
      slug: selectedItem.slug,
    });
    return () => {
      cancelled = true;
    };
  }, [category, selectedItem?.slug, selectedItem?.views, selectedItem?.cmsId, selectedItem?.key]);

  const articleNotFound = hydrated && Boolean(articleSlug) && !selectedItem;
  const isResolvingArticle = Boolean(articleSlug) && !selectedItem && !articleNotFound;

  const categoryHref = getGuideCategoryHref(category);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="sticky top-20 z-40 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 text-sm">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-1.5 font-medium text-gray-500 transition-colors hover:text-blue-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </button>
            ) : (
              <Link
                href="/"
                className="flex items-center gap-1.5 font-medium text-gray-500 transition-colors hover:text-blue-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Link>
            )}
            <span className="text-gray-300">/</span>
            {onBackToCategory ? (
              <button
                type="button"
                onClick={onBackToCategory}
                className="font-medium font-poppins text-gray-500 transition-colors hover:text-blue-600"
              >
                {categoryLabel}
              </button>
            ) : (
              <Link
                href={categoryHref}
                className="font-medium font-poppins text-gray-500 transition-colors hover:text-blue-600"
              >
                {categoryLabel}
              </Link>
            )}
            {selectedItem ? (
              <>
                <span className="text-gray-300">/</span>
                <span className="max-w-xs truncate font-semibold font-poppins text-gray-900">
                  {resolvedTitle}
                </span>
              </>
            ) : null}
            </div>
            {selectedItem?.cmsId ? (
              <CmsPublicEditButton
                href={`/painel/guias/${selectedItem.cmsId}/edit`}
                permission={['guides.edit', 'guides.review']}
                label="Editar guia"
                tone="light"
                variant="inline"
                className="shrink-0"
              />
            ) : null}
          </div>
        </div>
      </div>

      <ArticlesSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        categoryId={category}
        categoryLabel={categoryLabel}
        articles={filteredArticles}
        selectedSlug={articleSlug}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
      />

      {!sidebarOpen ? (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className={cn(
            'fixed left-0 top-36 z-30 flex items-center gap-1.5 rounded-r-xl border border-l-0 border-gray-200 bg-white py-2.5 pl-2 pr-3 shadow-md transition-all',
            'font-poppins text-xs font-semibold text-gray-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 hover:shadow-lg'
          )}
          aria-expanded={sidebarOpen}
          aria-controls="articles-sidebar"
        >
          <PanelLeftOpen className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Artigos</span>
        </button>
      ) : null}

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:py-12">
        {articleNotFound ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-lg sm:p-16">
            <div className="mx-auto max-w-md">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-900">Guia não encontrado</h3>
              <p className="mb-6 text-gray-600">
                Este artigo não existe nesta categoria ou ainda não foi publicado.
              </p>
              <Link
                href={categoryHref}
                className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar à categoria
              </Link>
            </div>
          </div>
        ) : selectedItem ? (
          <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
            <div className="px-6 py-8 sm:px-10 sm:py-10 md:px-12 md:py-12">
              <div className="mb-10">
                <div className="mb-6 flex flex-wrap items-center gap-3">
                  {selectedItem.featured || selectedItem.popular ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-700">
                      <Star className="h-4 w-4 fill-amber-600" aria-hidden />
                      Artigo Popular
                    </span>
                  ) : null}
                  {selectedItem.recentlyUpdated ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1.5 text-sm font-semibold text-green-700">
                      <Calendar className="h-4 w-4" aria-hidden />
                      Atualizado recentemente
                    </span>
                  ) : null}
                </div>

                <h1 className="mb-6 font-poppins text-3xl font-bold leading-tight text-gray-900 md:text-4xl">
                  {resolvedTitle}
                </h1>

                {authorCardProps ? (
                  <ArticleAuthorCard
                    {...authorCardProps}
                    views={liveViews ?? authorCardProps.views ?? selectedItem.views}
                  />
                ) : null}
              </div>

              <div
                className={cn(
                  'prose prose-lg max-w-none md:prose-xl',
                  '[&_table]:text-[15px] md:[&_table]:text-base',
                  '[&_th]:px-4 [&_th]:py-3.5 md:[&_th]:px-5',
                  '[&_td]:px-4 [&_td]:py-3.5 md:[&_td]:px-5',
                  '[&_h2]:text-2xl md:[&_h2]:text-3xl',
                  '[&_h3]:text-lg md:[&_h3]:text-xl'
                )}
              >
                {(() => {
                  const content = resolvedBody;
                  if (content) {
                    return (
                      <>
                        <div className="mb-10 border-b border-gray-100 pb-10 text-left text-xl leading-relaxed text-gray-700 md:text-2xl md:leading-relaxed">
                          <ArticleHtmlChunk
                            html={content.intro}
                            className="text-xl leading-relaxed md:text-2xl md:leading-relaxed"
                          />
                        </div>
                        {isXatErrorsGuideArticle(category, articleSlug) ? (
                          <XatErrorsGuide />
                        ) : isXatTicketsGuideArticle(category, articleSlug) ? (
                          <XatTicketsGuide />
                        ) : (
                          content.blocks.map((block, idx) => renderArticleBlock(block, idx))
                        )}
                      </>
                    );
                  }
                  return (
                    <>
                      <p className="mb-8 text-xl leading-relaxed text-gray-700 md:text-2xl">
                        Este guia irá auxiliá-lo com todas as informações necessárias sobre
                        &ldquo;{resolvedTitle}&rdquo;.
                      </p>
                      <InfoBox title="Em breve">
                        <p>
                          O conteúdo detalhado deste artigo está sendo preparado pela equipe xat
                          Ajuda.
                        </p>
                      </InfoBox>
                    </>
                  );
                })()}
              </div>
            </div>

            <GuideRelatedSection categoryId={category} slug={selectedItem.slug} />

            <ArticleFeedbackSection
              categoryId={category}
              slug={selectedItem.slug}
              articleTitle={resolvedTitle}
            />
          </article>
        ) : isResolvingArticle ? (
          <div
            className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg"
            aria-busy="true"
            aria-label="Carregando artigo"
          >
            <div className="animate-pulse px-6 py-8 sm:px-10 sm:py-10 md:px-12 md:py-12">
              <div className="mb-10">
                <div className="mb-6 flex gap-3">
                  <div className="h-8 w-28 rounded-full bg-gray-200" />
                  <div className="h-8 w-40 rounded-full bg-gray-200" />
                </div>
                <div className="mb-6 h-10 w-3/4 max-w-xl rounded-lg bg-gray-200" />
                <div className="flex gap-6">
                  <div className="h-10 w-10 rounded-full bg-gray-200" />
                  <div className="space-y-2">
                    <div className="h-4 w-24 rounded bg-gray-200" />
                    <div className="h-3 w-20 rounded bg-gray-100" />
                  </div>
                </div>
              </div>
              <div className="space-y-4 border-t border-gray-100 pt-10">
                <div className="h-4 w-full rounded bg-gray-100" />
                <div className="h-4 w-full rounded bg-gray-100" />
                <div className="h-4 w-5/6 rounded bg-gray-100" />
                <div className="h-4 w-4/6 rounded bg-gray-100" />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-lg sm:p-16">
            <div className="mx-auto max-w-md">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-900">Selecione um artigo</h3>
              <p className="mb-6 text-gray-600">
                Abra a lista de artigos no canto esquerdo para escolher um guia desta seção.
              </p>
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
              >
                <PanelLeftOpen className="h-4 w-4" />
                Ver artigos
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
